// frontend/src/components/UniversalNav/UniversalNav.jsx

import React, { useState, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { 
    AppBar, Toolbar, Typography, Button, Box, Link,
    IconButton, Drawer, List, ListItem, ListItemText, Divider, Menu, MenuItem, Collapse, ListItemIcon
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { AuthContext } from '../../contexts/AuthContext';
import './UniversalNav.css';
import LanguageSwitcher from '../LanguageSwitcher/LanguageSwitcher';

import BusinessIcon from '@mui/icons-material/Business';
import StorefrontIcon from '@mui/icons-material/Storefront';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';

const navLinks = [
    { 
        titleKey: 'nav.solutions', 
        subItems: [
            { 
                titleKey: 'nav.solutionsStandard', 
                fallback: 'Cross-Industry', 
                path: '/solutions-standard',
                icon: <BusinessIcon fontSize="small" /> 
            },
            { 
                titleKey: 'nav.solutionsAmazon', 
                fallback: 'Amazon Ecosystem', 
                path: '/solutions-amazon',
                icon: <StorefrontIcon fontSize="small" /> 
            },
            { 
                titleKey: 'nav.solutionsFinance', 
                fallback: 'Financial Services', 
                path: '/solutions-finance',
                icon: <AccountBalanceIcon fontSize="small" /> 
            },
            { 
                titleKey: 'nav.solutionsHealthcare', 
                fallback: 'Healthcare & Biotech', 
                path: '/solutions-healthcare',
                icon: <HealthAndSafetyIcon fontSize="small" /> 
            },
        ]
    },
    { titleKey: 'nav.enterprise', path: '/enterprise' },
    { titleKey: 'nav.features', path: '/features' },
    { titleKey: 'nav.technology', path: '/technology' },
    { titleKey: 'nav.howWeAreDifferent', path: '/how-we-are-different' },
    { titleKey: 'nav.pricing', path: '/pricing' },
    { titleKey: 'nav.about', path: '/about' },
    { titleKey: 'nav.blog', path: '/blog' },
    { titleKey: 'nav.help', path: '/contact' }
];

const UniversalNav = () => {
    const { t } = useTranslation(); // Initialize hook
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);
    const { isAuthenticated } = useContext(AuthContext);
    const location = useLocation();

    // States for dropdown and mobile collapse
    const [solutionsAnchorEl, setSolutionsAnchorEl] = useState(null);
    const [mobileSolutionsOpen, setMobileSolutionsOpen] = useState(false);

    useEffect(() => {
        const isLandingPage = location.pathname === '/';
        
        const setupScrollListener = () => {
            const scrollableElement = isLandingPage 
                ? document.querySelector('.landing-page-container')
                : document.querySelector('.app-wrapper');

            if (!scrollableElement) {
                requestAnimationFrame(setupScrollListener);
                return;
            }

            const handleScroll = () => {
                setIsScrolled(scrollableElement.scrollTop > 10);
            };

            handleScroll();
            scrollableElement.addEventListener('scroll', handleScroll);

            return () => {
                scrollableElement.removeEventListener('scroll', handleScroll);
            };
        };

        const cleanup = setupScrollListener();
        return cleanup;

    }, [location.pathname]);

    const logoLinkTarget = isAuthenticated ? '/app' : '/';

    const toggleDrawer = (open) => (event) => {
        if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
            return;
        }
        setDrawerOpen(open);
    };

    const handleSolutionsMenuOpen = (event) => {
        setSolutionsAnchorEl(event.currentTarget);
    };

    const handleSolutionsMenuClose = () => {
        setSolutionsAnchorEl(null);
    };

    const toggleMobileSolutions = (e) => {
        e.stopPropagation();
        setMobileSolutionsOpen(!mobileSolutionsOpen);
    };

    const drawerContent = (
        <Box
            sx={{ width: 250, display: 'flex', flexDirection: 'column', height: '100%' }}
            role="presentation"
            onKeyDown={toggleDrawer(false)}
        >
            <List sx={{ flexGrow: 1, paddingTop: '1rem' }}>
                {navLinks.map((item) => {
                    // Mobile Dropdown Item
                    if (item.subItems) {
                        const isGroupActive = item.subItems.some(sub => location.pathname === sub.path);
                        return (
                            <React.Fragment key={item.titleKey}>
                                <ListItem 
                                    button 
                                    onClick={toggleMobileSolutions}
                                    sx={{
                                        '&.Mui-selected': {
                                            backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                            borderLeft: '4px solid var(--secondary-color)',
                                            '& .MuiListItemText-primary': { color: 'var(--secondary-color)', fontWeight: 'bold' }
                                        }
                                    }}
                                    selected={isGroupActive && !mobileSolutionsOpen}
                                >
                                    <ListItemText primary={t(item.titleKey)} sx={{ color: isGroupActive ? 'var(--secondary-color)' : 'inherit' }} />
                                    {mobileSolutionsOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                </ListItem>
                                <Collapse in={mobileSolutionsOpen} timeout="auto" unmountOnExit>
                                    <List component="div" disablePadding>
                                        {item.subItems.map((sub) => {
                                            const isSubActive = location.pathname === sub.path;
                                            return (
                                                <ListItem 
                                                    button 
                                                    component={RouterLink} 
                                                    to={sub.path} 
                                                    key={sub.titleKey}
                                                    onClick={toggleDrawer(false)}
                                                    selected={isSubActive}
                                                    sx={{
                                                        pl: 4,
                                                        '&.Mui-selected': {
                                                            backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                                            borderLeft: '4px solid var(--secondary-color)',
                                                            '& .MuiListItemText-primary': { color: 'var(--secondary-color)', fontWeight: 'bold' }
                                                        }
                                                    }}
                                                >
                                                    {sub.icon && (
                                                        <ListItemIcon sx={{ minWidth: '36px', color: isSubActive ? 'var(--secondary-color)' : 'inherit' }}>
                                                            {sub.icon}
                                                        </ListItemIcon>
                                                    )}
                                                    <ListItemText primary={t(sub.titleKey, sub.fallback)} />
                                                </ListItem>
                                            );
                                        })}
                                    </List>
                                </Collapse>
                            </React.Fragment>
                        );
                    }

                    // Mobile Standard Item
                    const isActive = location.pathname === item.path;
                    return (
                        <ListItem 
                          button 
                          onClick={toggleDrawer(false)}
                          component={RouterLink} 
                          to={item.path} 
                          key={item.titleKey}
                          selected={isActive}
                          // Replace the hardcoded orange tint with a neutral grey,
                          // and let the border and text color carry the brand highlight.
                          sx={{
                            '&.Mui-selected': {
                                backgroundColor: 'rgba(0, 0, 0, 0.04)', // A neutral, light grey for the background
                                borderLeft: '4px solid var(--secondary-color)',
                                '& .MuiListItemText-primary': {
                                    color: 'var(--secondary-color)',
                                    fontWeight: 'bold',
                                },
                            },
                            '&.Mui-selected:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.08)', // A slightly darker grey on hover
                            }
                          }}
                        >
                            <ListItemText primary={t(item.titleKey)} />
                        </ListItem>
                    );
                })}
            </List>
            
            <Box>
                <Divider />
                <ListItem sx={{ padding: '1rem' }} onClick={toggleDrawer(false)}>
                    <Button 
                        component={RouterLink} 
                        to={isAuthenticated ? '/app' : '/login'}
                        variant="contained" 
                        color="secondary" 
                        fullWidth
                    >
                        {t(isAuthenticated ? 'nav.cta.app' : 'nav.cta.login')}
                    </Button>
                </ListItem>
            </Box>
        </Box>
    );

    const navBarClassName = `universal-nav-appbar ${isScrolled ? 'scrolled' : ''}`;

    return (
        <AppBar position="sticky" className={navBarClassName}>
           <Toolbar>
                <Box component={RouterLink} to={logoLinkTarget} className="universal-nav-logo-container">
                    <img src="/static/vetraai_logo_grey.png" alt="Vetra AI Logo" className="universal-nav-logo" />
                    <Typography variant="h6" className="universal-nav-title">
                        Vetra AI
                    </Typography>
                </Box>
                <Box className="universal-nav-links-desktop" >
                    {navLinks.map((item) => {
                        // Desktop Dropdown Item
                        if (item.subItems) {
                            const isGroupActive = item.subItems.some(sub => location.pathname === sub.path);
                            const menuOpen = Boolean(solutionsAnchorEl);
                            return (
                                <Box key={item.titleKey} sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Box
                                        onClick={handleSolutionsMenuOpen}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            fontWeight: 500,
                                            cursor: 'pointer',
                                            // 1. Text remains white instead of turning blue
                                            color: 'white',
                                            transition: 'color 0.2s ease-in-out',
                                            '&:hover': {
                                                color: '#e0e0e0',
                                            },
                                        }}
                                    >
                                        {/* 2. Apply padding and border ONLY to the text wrapper, not the icon */}
                                        <Box 
                                            component="span" 
                                            sx={{
                                                paddingBottom: '4px',
                                                paddingTop: '4px',
                                                borderBottom: (isGroupActive || menuOpen)
                                                    ? '2px solid var(--secondary-color)' 
                                                    : '2px solid transparent',
                                                transition: 'border-bottom 0.2s ease-in-out',
                                            }}
                                        >
                                            {t(item.titleKey)}
                                        </Box>
                                        <KeyboardArrowDownIcon sx={{ 
                                            ml: 0.5, 
                                            fontSize: '1.2rem', 
                                            transition: 'transform 0.2s', 
                                            transform: menuOpen ? 'rotate(180deg)' : 'none' 
                                        }} />
                                    </Box>
                                    <Menu
                                        anchorEl={solutionsAnchorEl}
                                        open={menuOpen}
                                        onClose={handleSolutionsMenuClose}
                                        MenuListProps={{ onMouseLeave: handleSolutionsMenuClose }}
                                        PaperProps={{
                                            sx: {
                                                mt: 1.5,
                                                bgcolor: '#171F29',
                                                border: '1px solid rgba(255,255,255,0.1)',
                                                color: 'white',
                                                boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
                                            }
                                        }}
                                    >
                                        {item.subItems.map((sub) => {
                                            const isSubActive = location.pathname === sub.path;
                                            return (
                                                <MenuItem 
                                                    key={sub.path} 
                                                    component={RouterLink} 
                                                    to={sub.path} 
                                                    onClick={handleSolutionsMenuClose}
                                                    sx={{
                                                        fontSize: '0.95rem',
                                                        // 3. Changed from blue to high-contrast white/grey
                                                        color: isSubActive ? 'white' : '#cccccc',
                                                        fontWeight: isSubActive ? 'bold' : 'normal',
                                                        backgroundColor: isSubActive ? 'rgba(255,255,255,0.08)' : 'transparent',
                                                        paddingTop: '10px',
                                                        paddingBottom: '10px',
                                                        '&:hover': { 
                                                            backgroundColor: 'rgba(255,255,255,0.12)',
                                                            color: 'white',
                                                            '& .MuiListItemIcon-root': { color: 'white' }
                                                        }
                                                    }}
                                                >
                                                    {sub.icon && (
                                                        <ListItemIcon sx={{ minWidth: '32px', color: isSubActive ? 'white' : '#cccccc' }}>
                                                            {sub.icon}
                                                        </ListItemIcon>
                                                    )}
                                                    {t(sub.titleKey, sub.fallback)}
                                                </MenuItem>
                                            );
                                        })}
                                    </Menu>
                                </Box>
                            );
                        }

                        // Desktop Standard Item
                        const isActive = location.pathname === item.path;
                        return (
                            <Link 
                                component={RouterLink} 
                                to={item.path} 
                                key={item.titleKey}
                                sx={{
                                    fontWeight: 500,
                                    // Apply padding to ALL links
                                    paddingBottom: '4px', 
                                    paddingTop: '4px',
                                    textDecoration: 'none',
                                    // The text color changes based on the active state
                                    color: isActive ? 'var(--secondary-color)' : 'white',
                                    // The border is ALWAYS there, just its color changes.
                                    // For inactive links, it's transparent, taking up space but being invisible.
                                    borderBottom: isActive 
                                        ? '2px solid var(--secondary-color)' 
                                        : '2px solid transparent',
                                    transition: 'color 0.2s ease-in-out, border-bottom 0.2s ease-in-out',
                                    '&:hover': {
                                        // Keep the hover color logic
                                        color: isActive ? 'var(--secondary-color)' : '#e0e0e0',
                                    },
                                }}
                            >
                                {t(item.titleKey)}
                            </Link>
                        );
                    })}

                </Box>
                <Box className="universal-nav-actions">
                    <LanguageSwitcher />
                     <Button 
                        component={RouterLink} 
                        to={isAuthenticated ? '/app' : '/login'}
                        variant="contained" 
                        color="secondary" 
                        className="desktop-cta"
                    >
                        {t(isAuthenticated ? 'nav.cta.app' : 'nav.cta.login')}
                    </Button>
                    
                    <IconButton
                        color="inherit"
                        aria-label="open drawer"
                        edge="end"
                        onClick={toggleDrawer(true)}
                        className="mobile-menu-button"
                    >
                        <MenuIcon />
                    </IconButton>
                </Box>
                <Drawer
                    anchor="right"
                    open={drawerOpen}
                    onClose={toggleDrawer(false)}
                >
                    {drawerContent}
                </Drawer>
            </Toolbar>
        </AppBar>
    );
};

export default UniversalNav;